/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { NgStyle } from "@angular/common";
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AgGridAngular } from "ag-grid-angular";
import { GridOptions, RowDataUpdatedEvent } from "ag-grid-community";
import { formatNumberMaxDecimals } from "../../utils/utils";
import { ButtonComponent } from "../button/button.component";
import { LoadingOverlayComponent } from "./overlays/loading-overlay/loading-overlay.component";
import { NoRowsOverlayComponent } from "./overlays/no-rows-overlay/no-rows-overlay.component";
import { agGridTheme, headerHeight, rowHeight } from "./table-style";

@Component({
	selector: "globalictcc-table",
	standalone: true,
	imports: [AgGridAngular, ButtonComponent, NgStyle],
	templateUrl: "./table.component.html",
	styleUrl: "./table.component.scss"
})
export class TableComponent implements OnInit {
	@Input() public gridOptions: GridOptions = {};
	@Input() public hasError: boolean = false;

	@Output() public handleTableRefresh = new EventEmitter<void>();

	public gridHeight: number;

	constructor(private _cdr: ChangeDetectorRef) {}

	private _defaultGridOptions: GridOptions = {
		theme: agGridTheme,
		suppressCellFocus: true,
		suppressMovableColumns: true,
		suppressPaginationPanel: true,
		cellSelection: false,
		suppressRowDrag: true,
		suppressMultiSort: true,
		loadingOverlayComponent: LoadingOverlayComponent,
		noRowsOverlayComponent: NoRowsOverlayComponent,
		defaultColDef: {
			minWidth: 200,
			maxWidth: 500,
			flex: 1,
			valueFormatter: params => {
				const value = params.value;
				return typeof value === "number" && Number.isFinite(value)
					? formatNumberMaxDecimals(value, 2)
					: (value ?? "");
			},
			cellStyle: {
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
				display: "flex",
				alignItems: "center"
			},
			comparator: () => 0
		}
	};

	ngOnInit() {
		this.gridOptions = {
			...this._defaultGridOptions,
			...{
				...this.gridOptions,
				onRowDataUpdated: (event: RowDataUpdatedEvent) => {
					const displayedRows = event.api.getDisplayedRowCount();
					const horizontalScrollbarHeight = 5;

					this.gridHeight =
						displayedRows > 0
							? displayedRows * rowHeight +
								(displayedRows - 1) +
								headerHeight +
								horizontalScrollbarHeight
							: 2 * (headerHeight + rowHeight);
					this._cdr.detectChanges();
				},
				columnDefs: this.gridOptions.columnDefs?.map(colDef => ({
					...colDef,
					resizable: false
				}))
			}
		};
	}
}
